import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { uniq, uniqBy } from 'ramda'
import { ActionType } from 'typesafe-actions'
import { NAMESPACE } from './constants'
import {
  TAddToGroupRequest,
  TAgencyEntity,
  TAgencyIdRequest,
  TAgencyInvitation,
  TAgencyInvitationByToken,
  TAgencyInviteTokenParams,
  TAgencyListingItem,
  TAgencyMember,
  TAgencySegment,
  TAgencyState,
  TAgencyTheme,
  TDeleteSegmentRequest,
  TInvitableParty,
  TRemoveFromGroupRequest,
  TResendInviteRequest,
  TRevokeInviteRequest,
  TSegmentRequest,
  TSendInviteRequest,
  TSendPartyInviteRequest,
  TSignupInvite,
  TUpdateAgencyRequest,
  TUpdateBrandingRequest,
} from './types'

const initialState: TAgencyState = {
  byId: {},
  allIds: [],
  invitations: [],
  invitationsByToken: {},
  signupInvite: null,
  users: [],
  themes: [],
  agencyListing: [],
  invitableParties: [],
  segments: [],
}

const agencySlice = createSlice({
  name: `${NAMESPACE}Api`,
  initialState,
  reducers: {
    agency_request: (state, { payload }: PayloadAction<TAgencyIdRequest>) => {},
    agency_success: (state, { payload }: PayloadAction<TAgencyEntity>) => {
      state.allIds = uniq([...state.allIds, payload.id])
      state.byId[payload.id] = payload
    },
    invitations_request: () => {},
    invitations_success: (state, { payload }: PayloadAction<TAgencyInvitation[]>) => {
      state.invitations = payload
    },
    invite_request: (state, { payload }: PayloadAction<TAgencyInviteTokenParams>) => {},
    invite_success: {
      reducer: (
        state,
        { payload, meta }: PayloadAction<TAgencyInvitationByToken, string, TAgencyInviteTokenParams>,
      ) => {
        if (meta.token) {
          state.invitationsByToken[meta.token] = payload
        }
      },
      prepare: (payload: TAgencyInvitationByToken, meta: TAgencyInviteTokenParams) => ({ payload, meta, error: null }),
    },
    sendInvite_request: (state, { payload }: PayloadAction<TSendInviteRequest>) => {},
    sendInvite_success: (state, { payload }: PayloadAction<{ id: string }>) => {},
    sendPartyInvite_request: (state, { payload }: PayloadAction<TSendPartyInviteRequest>) => {},
    sendPartyInvite_success: (state, { payload }: PayloadAction<{ id: string }>) => {},
    resendInvite_request: (state, { payload }: PayloadAction<TResendInviteRequest>) => {},
    resendInvite_success: (state, { payload }: PayloadAction<TAgencyInvitation>) => {
      state.invitations = state.invitations.map(invite => (invite.partyId === payload.partyId ? payload : invite))
    },
    revokeInvite_request: (state, { payload }: PayloadAction<TRevokeInviteRequest>) => {},
    revokeInvite_success: (state, { payload }: PayloadAction<TAgencyInvitation>) => {
      /** @todo update revokedAt with response? */
      state.invitations = state.invitations.map(invite => (invite.partyId === payload.partyId ? payload : invite))
      // state.invitations = state.invitations.filter(invite => invite.partyId !== payload.partyId)
    },
    signupInvite_request: (state, { payload }: PayloadAction<TAgencyInviteTokenParams>) => {},
    signupInvite_success: (state, { payload }: PayloadAction<TSignupInvite>) => {},
    users_request: () => {},
    users_success: (state, { payload }: PayloadAction<TAgencyMember[]>) => {
      state.users = uniqBy(m => m.userId, [...state.users, ...payload])
    },
    themes_request: () => {},
    themes_success: (state, { payload }: PayloadAction<TAgencyTheme[]>) => {
      state.themes = payload
    },
    updateBranding_request: (state, { payload }: PayloadAction<TUpdateBrandingRequest>) => {},
    updateBranding_success: (state, { payload }: PayloadAction<null>) => {},
    themeSet: (state, { payload }: PayloadAction<TAgencyTheme>) => {},
    logoUpdated: (state, { payload }: PayloadAction<{ logoUrl: string }>) => {},
    updateAgency_request: (state, { payload }: PayloadAction<TUpdateAgencyRequest>) => {},
    updateAgency_success: (state, { payload }: PayloadAction<TAgencyEntity>) => {
      state.byId[payload.id] = payload
    },
    agencyListing_request: () => undefined,
    agencyListing_success: (state, { payload }: PayloadAction<TAgencyListingItem[]>) => {
      state.agencyListing = payload
    },
    invitableParties_request: () => {},
    invitableParties_success: (state, { payload }: PayloadAction<TInvitableParty[]>) => {
      state.invitableParties = payload
    },
    getSegments_request: () => {},
    getSegments_success: (state, { payload }: PayloadAction<TAgencySegment[]>) => {
      state.segments = payload
    },
    createSegment_request: (state, { payload }: PayloadAction<TSegmentRequest>) => {},
    createSegment_success: (state, { payload }: PayloadAction<TAgencySegment[]>) => {
      state.segments = payload
    },
    updateSegment_request: (state, { payload }: PayloadAction<TAgencySegment>) => {},
    updateSegment_success: (state, { payload }: PayloadAction<TAgencySegment[]>) => {
      state.segments = payload
    },
    deleteSegment_request: (state, { payload }: PayloadAction<TDeleteSegmentRequest>) => {},
    deleteSegment_success: (state, { payload }: PayloadAction<TAgencySegment[]>) => {
      state.segments = payload
    },
    deactivateAgency_request: (state, { payload }: PayloadAction<TAgencyIdRequest>) => {},
    deactivateAgency_success: (state, { payload }: PayloadAction<TAgencyIdRequest>) => {
      state.agencyListing = state.agencyListing.map(agency =>
        agency.agencyId === payload.id
          ? {
              ...agency,
              isActive: false,
            }
          : agency,
      )
    },
    /**
     * NB this writes on the *request*, not the success — activation is applied
     * optimistically where deactivation waits for the response.
     */
    activateAgency_request: (state, { payload }: PayloadAction<TAgencyIdRequest>) => {
      state.agencyListing = state.agencyListing.map(agency =>
        agency.agencyId === payload.id
          ? {
              ...agency,
              isActive: true,
            }
          : agency,
      )
    },
    activateAgency_success: (state, { payload }: PayloadAction<TAgencyIdRequest>) => {},
    addToGroup_request: (state, { payload }: PayloadAction<TAddToGroupRequest>) => {},
    addToGroup_success: (state, { payload }: PayloadAction<unknown>) => {},
    removeFromGroup_request: (state, { payload }: PayloadAction<TRemoveFromGroupRequest>) => {},
    removeFromGroup_success: (state, { payload }: PayloadAction<unknown>) => {},
    /** @todo move this out of API module */
    existingInviteNotification: () => {},
    failedInviteNotification: () => {},
  },
})

const { reducer, actions: events } = agencySlice

export type AgencyEvent = ActionType<typeof events>

export { events, initialState, reducer }
